import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Social Media Management",
  "Website Design & Development",
  "Brand Identity & Logo Design",
  "Content Creation & Copywriting",
  "Graphic Design",
  "SEO & Local SEO",
  "Performance Marketing (Google & Meta Ads)",
  "AI Chatbots & Business Automation",
  "Video Editing & AI Content Production",
  "Lead Generation Strategy",
  "UGC Ads",
  "Influencer Management & Marketing",
  "Something else",
];

const inputCls =
  "w-full bg-black border border-white/10 rounded-md px-4 py-3.5 text-sm text-white placeholder:text-[#A6A6B0]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Enquiry received. We'll be in touch soon.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(" ")
            : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
              Contact
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
              Tell us your <span className="text-gradient-purple">next move.</span>
            </h2>
            <p className="mt-7 text-base sm:text-lg text-[#A6A6B0] leading-relaxed max-w-md">
              Tell us where your brand is today and where it needs to go. We respond to every
              enquiry personally.
            </p>
            <div className="mt-10 space-y-4 text-sm text-[#A6A6B0]">
              <p className="flex items-center gap-3">
                <span className="w-8 h-px bg-gradient-to-r from-[#6D00FF] to-[#C43CFF]" aria-hidden="true" />
                Replies within one business day
              </p>
              <p className="flex items-center gap-3">
                <span className="w-8 h-px bg-gradient-to-r from-[#6D00FF] to-[#C43CFF]" aria-hidden="true" />
                No pitch decks required — just your idea
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="glass-card rounded-lg p-7 sm:p-9 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-company" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
                    Company
                  </label>
                  <input
                    id="contact-company"
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Brand or company"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-service" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
                    What can we build together?
                  </label>
                  <select
                    id="contact-service"
                    data-testid="contact-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className={`${inputCls} appearance-none ${form.service ? "" : "text-[#A6A6B0]/50"}`}
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="bg-black text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
                  Your next move *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your brand, your goals and what you want to build."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group w-full flex items-center justify-center gap-3 bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-md transition-[background-color,box-shadow,opacity] duration-300 hover:shadow-[0_0_40px_-8px_rgba(139,44,255,0.8)]"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Send Enquiry
                    <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
